import { CacheStrategy, DataSourceStrategy } from '@rammetec/data-access';
import { addressFieldDescriptors } from './_addressFields.js';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { LIFECYCLE_FIELD_DESCRIPTORS } from './_lifecycleFields.js';
import { SYSTEM_ALLOWED_DATA_SOURCES } from './dataSourcePolicy.js';
import { ORGANIZATION_DATA, USER_ORGANIZATION, } from '../types/organization.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for OrganizationData entity.
 * Stored in the global 'org' collection.
 *
 * Fields without groups are always persisted.
 * Fields with groups are persisted only when those groups are active.
 */
export const organizationDataDescriptor = {
    name: ORGANIZATION_DATA,
    label: 'Organização',
    domain: 'identity',
    collectionPath: 'org',
    version: 1,
    description: 'Represents an organization (company/tenant) in the system',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Unique organization identifier',
        },
        {
            name: 'name',
            example: 'Têxtil Vale Verde Ltda',
            label: 'Nome',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
            description: 'Organization full name',
        },
        {
            name: 'abbreviation',
            example: 'Vale Verde',
            label: 'Abreviação',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.ABBREVIATION,
            description: 'Short abbreviation for the organization',
        },
        {
            name: 'emailDomain',
            example: 'valeverde.example',
            label: 'Domínio de e-mail',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.EMAIL_DOMAIN,
            description: 'Company email domain or URL (e.g. empresa.com.br) used to compose addresses for auto-created users',
        },
        {
            name: 'cnpj',
            example: '12345678000195',
            label: 'CNPJ',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CNPJ,
            // No groups = always persisted
            description: 'Organization CNPJ (14 chars, alphanumeric-safe via normalizeCnpj) — fiscal id / own-issuance detection',
        },
        // Standard address block, reference mode (spec 0047 §4.1 / D22) — the issuer
        // of the fiscal document needs one. Firestore entity: declaring the fields is
        // the whole adoption, with no schema to sync and no column to create.
        ...addressFieldDescriptors(),
        {
            name: 'holding',
            label: 'Holding',
            type: 'string',
            required: false,
            description: 'Parent holding company ID (self-referencing)',
        },
        {
            name: 'logoUrl',
            label: 'URL do logo',
            type: 'string',
            required: false,
            description: 'URL of the organization logo image',
        },
        {
            name: 'planIds',
            label: 'IDs dos planos',
            type: 'array',
            arrayItemType: 'string',
            required: false,
            description: 'Contracted Plan ids (commercial entitlement). Spec 0038 §3.2',
        },
        {
            name: 'warning',
            label: 'Mensagem de aviso',
            type: 'string',
            required: false,
            description: 'Optional warning message displayed in the console header for this organization',
        },
        ...LIFECYCLE_FIELD_DESCRIPTORS,
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        ...AUDIT_FOREIGN_KEYS,
        {
            fieldName: 'holding',
            targetEntity: 'OrganizationData',
            relationship: 'one-to-one',
            displayField: 'name',
            description: 'Self-referencing to parent holding company',
        },
    ],
};
/**
 * Descriptor for UserOrganization entity.
 * Stored in 'user/{userId}/orgs/{orgId}'.
 * Represents the link between a user and an organization.
 */
export const userOrganizationDescriptor = {
    name: USER_ORGANIZATION,
    label: 'Vínculo usuário–organização',
    domain: 'identity',
    collectionPath: 'user/{userId}/orgs',
    pathPatternKeys: {
        userId: 'string',
    },
    version: 1,
    description: 'Links a user to an organization with role information',
    repositoryConfig: {
        dataSource: DataSourceStrategy.Firestore,
        cache: CacheStrategy.InMemory,
    },
    allowedDataSources: SYSTEM_ALLOWED_DATA_SOURCES,
    fields: [
        {
            name: 'id',
            label: 'ID',
            type: 'string',
            required: true,
            // No groups = always persisted
            description: 'Composite ID: ${userId}_${orgId}',
        },
        {
            name: 'userId',
            label: 'ID do usuário',
            type: 'string',
            required: true,
            description: 'Reference to the user',
        },
        {
            name: 'orgId',
            label: 'ID da organização',
            type: 'string',
            required: true,
            description: 'Reference to the organization',
        },
        {
            name: 'role',
            label: 'Papel',
            type: 'enum',
            required: true,
            enumValues: ['system', 'admin', 'supervisor', 'user'],
            description: 'User role in this organization',
        },
        {
            name: 'aiAssistantEnabled',
            label: 'Assistente de IA',
            type: 'boolean',
            required: false,
            description: 'Whether the AI assistant is enabled for this user in this org. Absent = enabled.',
        },
        {
            name: 'inactivationDate',
            label: 'Data de inativação',
            type: 'date',
            required: false,
            description: 'Date when this link was inactivated (null if active)',
        },
    ],
    foreignKeys: [
        {
            fieldName: 'userId',
            targetEntity: 'UserData',
            relationship: 'one-to-one',
            description: 'The user in this relationship',
            // Label-only (0167 §11) — same reason as the audit user FKs.
            expand: false,
        },
        {
            fieldName: 'orgId',
            targetEntity: 'OrganizationData',
            relationship: 'one-to-one',
            description: 'The organization in this relationship',
        },
    ],
};
//# sourceMappingURL=organization.descriptor.js.map